import { palette } from '../../theme';
import { usePortalPalette } from '../../theme/usePortalPalette';
import { Box, Button, IconButton, Stack, Typography } from '../../primitives';
import * as Icons from '../../icons';
import { iconSize } from '@munifocus/tokens';

export type CallBarPhase = 'ringing-out' | 'ringing-in' | 'active';

export interface CallBarProps {
  phase: CallBarPhase;
  /** The other party. Falls back to the label below when unknown. */
  peerName: string | null;
  /** Seconds connected; only shown while active. */
  elapsedSeconds: number;
  muted: boolean;
  onAccept: () => void;
  onDecline: () => void;
  onHangUp: () => void;
  onToggleMute: () => void;
  labels: {
    incoming: string;
    calling: string;
    unknownCaller: string;
    accept: string;
    decline: string;
    hangUp: string;
    mute: string;
    unmute: string;
  };
}

/**
 * The call surface: one bar above the composer, in all three states.
 *
 * A bar rather than a modal. An officer on a call is usually reading the file
 * they are calling about, and a dialog that covers the thread would force them
 * to choose between the conversation and the record it concerns.
 *
 * Audio only — there is no video tile or screen-share control, because Twilio
 * Programmable Voice carries neither.
 */
export function CallBar({
  phase,
  peerName,
  elapsedSeconds,
  muted,
  onAccept,
  onDecline,
  onHangUp,
  onToggleMute,
  labels,
}: CallBarProps) {
  const portal = usePortalPalette();
  const name = peerName ?? labels.unknownCaller;
  const incoming = phase === 'ringing-in';

  return (
    <Stack
      direction="row"
      // Assertive only for an incoming call — that is the one state the officer
      // did not cause and must be interrupted for. Announcing their *own*
      // outgoing call assertively talks over whatever they were reading, for
      // news they already have; `status` is polite and waits its turn.
      role={incoming ? 'alert' : 'status'}
      sx={{
        alignItems: 'center',
        gap: 1.5,
        px: 2,
        py: 1.25,
        backgroundColor: portal.brand.rail,
        color: palette.text.onDark,
        borderTop: `1px solid ${palette.border.onDarkFaint}`,
      }}
    >
      <Icons.Phone size={iconSize.lg} aria-hidden />

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="subtitle2" noWrap sx={{ color: palette.text.onDark }}>
          {name}
        </Typography>
        <Typography variant="caption" sx={{ color: palette.text.onDarkSubtle }}>
          {phase === 'active'
            ? formatClock(elapsedSeconds)
            : incoming
              ? labels.incoming
              : labels.calling}
        </Typography>
      </Box>

      {incoming ? (
        <>
          <Button
            size="small"
            variant="contained"
            startIcon={<Icons.Phone size={iconSize.sm} aria-hidden />}
            onClick={onAccept}
          >
            {labels.accept}
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={onDecline}
            sx={{ color: palette.text.onDark, borderColor: palette.border.onDarkButton }}
          >
            {labels.decline}
          </Button>
        </>
      ) : (
        <>
          {phase === 'active' && (
            <IconButton
              onClick={onToggleMute}
              aria-label={muted ? labels.unmute : labels.mute}
              // Pressed state, not just an icon swap — "is my microphone off"
              // must be answerable without interpreting a glyph.
              aria-pressed={muted}
              sx={{ color: palette.text.onDark }}
            >
              {muted ? (
                <Icons.MicOff size={iconSize.lg} aria-hidden />
              ) : (
                <Icons.Mic size={iconSize.lg} aria-hidden />
              )}
            </IconButton>
          )}
          <IconButton
            onClick={onHangUp}
            aria-label={labels.hangUp}
            sx={{ color: palette.semantic.emergency }}
          >
            <Icons.PhoneOff size={iconSize.lg} aria-hidden />
          </IconButton>
        </>
      )}
    </Stack>
  );
}

/** `m:ss` up to an hour, then `h:mm:ss`. */
function formatClock(totalSeconds: number): string {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return hours > 0 ? `${hours}:${pad(minutes)}:${pad(seconds)}` : `${minutes}:${pad(seconds)}`;
}
